import { captureStencilRouteContent, readStencilRouteContentStatus } from './stencil-route-content.adapter';
import { hasPendingStencilRouteContent, hasStencilRouteContentChanged } from './stencil-route-content.business';
import {
    STENCIL_ROUTE_CONTENT_OBSERVER_OPTIONS,
    STENCIL_ROUTE_CONTENT_STABILITY_MS,
} from './stencil-route-content.constants';
import type { StencilRouteContentSnapshot } from './stencil-route-content.model';

interface StencilRouteContentObserverOptions {
    contentRoot: Element;
    startSnapshot?: StencilRouteContentSnapshot;
    onPending: VoidFunction;
    onReady: VoidFunction;
}

interface ObserverDisconnectFrames {
    first?: number;
    second?: number;
}

const cancelObserverDisconnect = (frames: ObserverDisconnectFrames) => {
    if (frames.first !== undefined) {
        globalThis.cancelAnimationFrame(frames.first);
    }
    if (frames.second !== undefined) {
        globalThis.cancelAnimationFrame(frames.second);
    }
    frames.first = undefined;
    frames.second = undefined;
};

const disconnectObserverAfterPaint = (observer: MutationObserver, frames: ObserverDisconnectFrames) => {
    const disconnect = () => observer.disconnect();
    const scheduleDisconnect = () => {
        frames.second = globalThis.requestAnimationFrame(disconnect);
    };

    frames.first = globalThis.requestAnimationFrame(scheduleDisconnect);
};

const containsContentMutation = (mutations: MutationRecord[]): boolean =>
    mutations.some((mutation) => mutation.type === 'childList' || mutation.type === 'characterData');

export const observeStencilRouteContent = ({
    contentRoot,
    startSnapshot,
    onPending,
    onReady,
}: StencilRouteContentObserverOptions): VoidFunction => {
    let routeContentCommitted =
        startSnapshot === undefined ||
        hasStencilRouteContentChanged(captureStencilRouteContent(contentRoot), startSnapshot);
    let stabilityTimeout: ReturnType<typeof globalThis.setTimeout> | undefined;
    const observerDisconnectFrames: ObserverDisconnectFrames = {};
    let observationVersion = 0;

    const cancelScheduledCheck = () => {
        if (stabilityTimeout !== undefined) {
            globalThis.clearTimeout(stabilityTimeout);
        }
        cancelObserverDisconnect(observerDisconnectFrames);
    };

    const scheduleStabilityCheck = () => {
        cancelScheduledCheck();
        const version = ++observationVersion;

        if (!routeContentCommitted || hasPendingStencilRouteContent(readStencilRouteContentStatus(contentRoot))) {
            onPending();
            return;
        }

        stabilityTimeout = globalThis.setTimeout(() => {
            if (
                version !== observationVersion ||
                hasPendingStencilRouteContent(readStencilRouteContentStatus(contentRoot))
            ) {
                scheduleStabilityCheck();
                return;
            }

            onReady();
            disconnectObserverAfterPaint(observer, observerDisconnectFrames);
        }, STENCIL_ROUTE_CONTENT_STABILITY_MS);
    };

    const observer = new MutationObserver((mutations) => {
        onPending();

        if (
            !routeContentCommitted &&
            startSnapshot !== undefined &&
            containsContentMutation(mutations) &&
            hasStencilRouteContentChanged(captureStencilRouteContent(contentRoot), startSnapshot)
        ) {
            routeContentCommitted = true;
        }

        scheduleStabilityCheck();
    });

    observer.observe(contentRoot, STENCIL_ROUTE_CONTENT_OBSERVER_OPTIONS);
    scheduleStabilityCheck();

    return () => {
        observationVersion++;
        cancelScheduledCheck();
        observer.disconnect();
    };
};
